import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentCard } from './AttachmentCard';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { cn } from '@/core/lib/utils';
import type { TaskAttachment } from '../../types';

interface TaskAttachmentsProps {
  attachments: TaskAttachment[];
  uploading?: boolean;
  onUpload: (files: FileList) => Promise<void>;
  onDelete: (id: string) => void;
}

export function TaskAttachments({
  attachments,
  uploading,
  onUpload,
  onDelete,
}: TaskAttachmentsProps) {
  const { t } = useTranslation('tasks');
  const inputRef = useRef<HTMLInputElement>(null);
  const { confirm, isOpen: confirmOpen, options, handleConfirm, handleCancel } = useConfirm();

  async function handleDelete(id: string) {
    const result = await confirm({
      title: t('attachments.confirmDelete.title'),
      description: t('attachments.confirmDelete.description'),
      confirmLabel: t('attachments.confirmDelete.confirm'),
      cancelLabel: t('attachments.confirmDelete.cancel'),
      variant: 'destructive',
    });
    if (result.confirmed) onDelete(id);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) onUpload(e.dataTransfer.files);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) {
      onUpload(e.target.files);
      e.target.value = '';
    }
  }

  return (
    <>
      <section data-testid="tasks-attachments" aria-label={t('attachments.title')}>
        <h3 className="mb-3 text-sm font-medium">{t('attachments.title')}</h3>

        {attachments.length > 0 && (
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {attachments.map((a) => (
              <AttachmentCard key={a.id} attachment={a} onDelete={handleDelete} />
            ))}
          </div>
        )}

        <div
          role="button"
          tabIndex={0}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => !uploading && inputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && !uploading && inputRef.current?.click()}
          data-testid="tasks-attachments-dropzone"
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-6 text-center transition-colors hover:border-primary/50 hover:bg-accent/30',
            uploading && 'pointer-events-none opacity-60',
          )}
        >
          <p className="text-xs text-muted-foreground">
            {uploading ? t('attachments.uploading') : t('attachments.dropzone')}
          </p>
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileChange}
          data-testid="tasks-attachments-file-input"
        />
      </section>

      {confirmOpen && options && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(v) => !v && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
          data-testid="tasks-attachment-delete-confirm"
        />
      )}
    </>
  );
}
